import { ValidationResult } from 'joi';

import { ValidationFunctionResult } from '../../../../shared/types/validation-function-result';
import { setStatusSchema } from '../../../../shared/validators/schemas/set-status.schema';
import { SupplierRecordDto } from '../../dtos/supplier-record.dto';
import { SupplierActionEnum } from '../../enums/supplier-action.enum';
import { SupplierStatusEnum } from '../../enums/supplier-status.enum';

/**
 * Validates if an action can be performed by validating the record data.
 * @param record - The record for the data validation.
 * @param action - The action to be validated.
 * @param abortEarly - Whether to abort the validation on the first error.
 * @returns The validation result.
 */
export const canPerformAction = (
	record: SupplierRecordDto,
	action: SupplierActionEnum,
	abortEarly: boolean = false,
): ValidationFunctionResult => {
	// Check if record is provided
	if (!record) throw new Error('record is required.');

	let validationResult: ValidationResult | undefined;

	const fixedStatuses = [
		SupplierStatusEnum.ACTIVE,
		SupplierStatusEnum.CLOSED,
		SupplierStatusEnum.ON_HOLD,
	];

	switch (action) {
		case SupplierActionEnum.ACTIVATE:
			validationResult = setStatusSchema(
				SupplierStatusEnum.ACTIVE,
				fixedStatuses,
			).validate(record, {
				abortEarly,
			});
			break;

		case SupplierActionEnum.CLOSE:
			validationResult = setStatusSchema(
				SupplierStatusEnum.CLOSED,
				fixedStatuses,
			).validate(record, {
				abortEarly,
			});
			break;

		case SupplierActionEnum.PUT_ON_HOLD:
			validationResult = setStatusSchema(
				SupplierStatusEnum.ON_HOLD,
				fixedStatuses,
			).validate(record, {
				abortEarly,
			});
			break;

		default:
			throw new Error(`Unknown action: ${action}`);
	}

	// Check for validation errors
	if (validationResult?.error) {
		return {
			isValid: false,
			errors: validationResult.error.details?.map((e) => e.message),
		};
	}

	// Return valid result if no errors
	return { isValid: true };
};
